import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../hooks/useAuth";
import { openCreateGifteeWizard } from "../createGifteeWizardNav.js";
import GifteeInterestsMoreSheet from "../components/GifteeInterestsMoreSheet.jsx";
import { useGiftees } from "../hooks/useGiftees";
import { useSavedRecommendations } from "../hooks/useSavedRecommendations";
import profileStyles from "../styles/GifteeProfile.module.css";
import styles from "../styles/GifteeSwipeRecommendations.module.css";
import {
  customInterestsFromSet,
  orderedSelectedPresetInterests,
  parseInterestsFromGiftee,
} from "../utils/gifteeInterests.js";
import {
  getSuggestionsForGiftee,
  savedRecommendationToCard,
} from "../utils/giftSuggestions.js";
import GifteeFlowHeader from "../components/GifteeFlowHeader.jsx";
import GifteeRecoSwipeCard from "../components/GifteeRecoSwipeCard.jsx";

function normTitle(t) {
  return (t || "").trim().toLowerCase();
}

const ROT_MAX = 11.88;
const COMMIT_PX = 90;
const EXIT_SHIFT = 520;

/** Discovery deck depth pulled from the Amazon catalog on top of saved cards. */
const DISCOVERY_DECK_SIZE = 12;

/**
 * Deck = the giftee's saved recommendations first, then interest-matched catalog
 * suggestions as discovery-only cards (not persisted on thumbs up).
 */
function buildDeck(giftee, savedRecommendations) {
  const saved = (savedRecommendations ?? []).map((rec, idx) => ({
    ...savedRecommendationToCard(rec, idx),
    id: `saved-${rec.id}`,
    savedDocId: rec.id,
  }));

  const seenAsins = new Set(saved.map((card) => card.asin).filter(Boolean));
  const seenTitles = new Set(saved.map((card) => normTitle(card.title)));

  const discovery = getSuggestionsForGiftee(giftee, {
    limit: DISCOVERY_DECK_SIZE,
  })
    .filter(
      (card) =>
        !seenAsins.has(card.asin) && !seenTitles.has(normTitle(card.title)),
    )
    .map((card) => ({ ...card, savedDocId: undefined }));

  return [...saved, ...discovery];
}

function rotationFromDrag(dx) {
  return Math.max(
    -ROT_MAX,
    Math.min(ROT_MAX, (-dx / COMMIT_PX) * ROT_MAX),
  );
}

export default function GifteeSwipeRecommendationsScreen() {
  const { gifteeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { giftees, loading: gifteesLoading } = useGiftees();
  const { savedRecommendations, loading: savedLoading } =
    useSavedRecommendations(gifteeId);

  const savedRef = useRef(savedRecommendations);
  useEffect(() => {
    savedRef.current = savedRecommendations;
  }, [savedRecommendations]);

  const reminderTitle = location.state?.reminderTitle;

  const giftee = giftees.find((g) => g.id === gifteeId);

  const customInterests = useMemo(() => {
    if (!giftee) return [];
    return customInterestsFromSet(parseInterestsFromGiftee(giftee));
  }, [giftee]);

  const [interestsMoreOpen, setInterestsMoreOpen] = useState(false);
  const [interestsDraft, setInterestsDraft] = useState(() => new Set());
  const [thingsToAvoidDraft, setThingsToAvoidDraft] = useState("");
  const [quickPromptsDraft, setQuickPromptsDraft] = useState("");
  const [recoFeedbackSaveBusy, setRecoFeedbackSaveBusy] = useState(false);

  useEffect(() => {
    if (!giftee) return;
    setThingsToAvoidDraft(
      typeof giftee.thingsToAvoid === "string" ? giftee.thingsToAvoid : "",
    );
    setQuickPromptsDraft(
      typeof giftee.quickPrompts === "string" ? giftee.quickPrompts : "",
    );
  }, [giftee]);

  useEffect(() => {
    setInterestsMoreOpen(false);
  }, [gifteeId]);

  const [deck, setDeck] = useState([]);
  const deckRef = useRef([]);
  /** Only seed deck once per giftee visit; avoids refilling when savedLoading flickers (auth / resubscribe). */
  const deckSeededForGifteeRef = useRef(null);

  useEffect(() => {
    deckRef.current = deck;
  }, [deck]);

  /**
   * Initial deck: giftee screen items (saved or defaults) plus discovery extras.
   * Do not run again for the same giftee while this instance is mounted — otherwise a
   * savedLoading true/false cycle would rebuild the full deck and swipes never “finish”.
   */
  useEffect(() => {
    if (savedLoading || gifteesLoading || !gifteeId || !giftee) return;
    if (deckSeededForGifteeRef.current === gifteeId) return;
    deckSeededForGifteeRef.current = gifteeId;
    setDeck(buildDeck(giftee, savedRecommendations));
    /* savedRecommendations: read once when loading clears for this giftee; omit dep so deletes do not reseed */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gifteeId, savedLoading, gifteesLoading, giftee]);

  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const startRef = useRef({ x: 0, y: 0 });
  const [exiting, setExiting] = useState(null);
  const exitDirRef = useRef(null);
  const [isLearnOpen, setIsLearnOpen] = useState(true);
  /** After a committed swipe, the new front card was the blurred back — animate to sharp once. */
  const [playFrontUnblur, setPlayFrontUnblur] = useState(false);

  useEffect(() => {
    setIsLearnOpen(true);
    setPlayFrontUnblur(false);
  }, [gifteeId]);

  const openInterestsMore = useCallback(() => {
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    setInterestsDraft(new Set(parseInterestsFromGiftee(g)));
    setInterestsMoreOpen(true);
  }, [giftees, gifteeId]);

  const toggleDraftInterestChip = useCallback((chip) => {
    setInterestsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });
  }, []);

  const saveInterestsMore = useCallback(async () => {
    if (!gifteeId || !currentUser?.uid) return;
    try {
      const { updateDoc, doc: docRef, serverTimestamp } = await import(
        "firebase/firestore"
      );
      await updateDoc(docRef(db, "users", currentUser.uid, "giftees", gifteeId), {
        interests: [...interestsDraft],
        updatedAt: serverTimestamp(),
      });
      setInterestsMoreOpen(false);
    } catch (e) {
      console.error("GifteeSwipeRecommendations interests persist:", e);
    }
  }, [gifteeId, currentUser?.uid, interestsDraft]);

  const removeProfileInterest = useCallback(
    async (label) => {
      if (!gifteeId || !currentUser?.uid) return;
      const g = giftees.find((x) => x.id === gifteeId);
      if (!g) return;
      const current = parseInterestsFromGiftee(g);
      if (current.size <= 1) return;
      if (!current.has(label)) return;
      current.delete(label);
      try {
        const { updateDoc, doc: docRef, serverTimestamp } = await import(
          "firebase/firestore"
        );
        await updateDoc(docRef(db, "users", currentUser.uid, "giftees", gifteeId), {
          interests: [...current],
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error("GifteeSwipeRecommendations remove interest:", e);
      }
    },
    [gifteeId, currentUser?.uid, giftees],
  );

  const onRecoFeedbackSave = useCallback(async () => {
    if (!gifteeId || !currentUser?.uid) return;
    setRecoFeedbackSaveBusy(true);
    try {
      const { updateDoc, doc: docRef, serverTimestamp } = await import(
        "firebase/firestore"
      );
      await updateDoc(docRef(db, "users", currentUser.uid, "giftees", gifteeId), {
        thingsToAvoid: thingsToAvoidDraft.trim(),
        quickPrompts: quickPromptsDraft.trim(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/giftee/${gifteeId}/more-recommendations/loading`, {
        replace: true,
        state: { reminderTitle },
      });
    } catch (e) {
      console.error("GifteeSwipeRecommendations feedback save:", e);
    } finally {
      setRecoFeedbackSaveBusy(false);
    }
  }, [
    gifteeId,
    currentUser?.uid,
    thingsToAvoidDraft,
    quickPromptsDraft,
    navigate,
    reminderTitle,
  ]);

  const deleteSavedIfMatched = useCallback(
    async (rec) => {
      if (!currentUser || !gifteeId || !rec) return;
      const docId =
        rec.savedDocId ||
        savedRef.current.find(
          (s) =>
            normTitle(s.title) === normTitle(rec.title) &&
            (s.priceText || "") === (rec.priceText || ""),
        )?.id;
      if (!docId) return;
      try {
        await deleteDoc(
          doc(
            db,
            "users",
            currentUser.uid,
            "giftees",
            gifteeId,
            "savedRecommendations",
            docId,
          ),
        );
      } catch (err) {
        console.error("GifteeSwipeRecommendations delete:", err);
      }
    },
    [currentUser, gifteeId],
  );

  const onPointerDown = (e) => {
    if (isLearnOpen || exiting || !deckRef.current[0]) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = true;
    setIsDragging(true);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDrag({ x: 0, y: 0 });
  };

  const onPointerMove = (e) => {
    if (isLearnOpen || !draggingRef.current || exiting) return;
    setDrag({
      x: e.clientX - startRef.current.x,
      y: e.clientY - startRef.current.y,
    });
  };

  const finishDrag = useCallback(() => {
    draggingRef.current = false;
    setIsDragging(false);
  }, []);

  const onPointerUp = (e) => {
    if (isLearnOpen || exiting || !deckRef.current[0]) return;
    if (!draggingRef.current) return;
    const dx = e.clientX - startRef.current.x;
    finishDrag();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    if (Math.abs(dx) < COMMIT_PX) {
      setDrag({ x: 0, y: 0 });
      return;
    }

    exitDirRef.current = dx > 0 ? "right" : "left";
    setExiting(exitDirRef.current);
    setDrag({
      x: dx > 0 ? EXIT_SHIFT : -EXIT_SHIFT,
      y: 0,
    });
  };

  const onPointerCancel = (e) => {
    if (isLearnOpen) return;
    finishDrag();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    setDrag({ x: 0, y: 0 });
  };

  const onFrontTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== "transform") return;
    if (!exiting) return;
    const dir = exitDirRef.current;
    const top = deckRef.current[0];
    if (dir === "left" && top) {
      deleteSavedIfMatched(top);
    }
    const willHaveNextTop = deckRef.current.length > 1;
    setDeck((d) => d.slice(1));
    exitDirRef.current = null;
    setExiting(null);
    setDrag({ x: 0, y: 0 });
    if (willHaveNextTop) {
      setPlayFrontUnblur(true);
    }
  };

  const headerTitle = (giftee?.name || "").trim() || "Giftee";

  if (gifteesLoading || savedLoading) {
    return (
      <div className={styles.container}>
        <GifteeFlowHeader title={headerTitle} />
        <p className={styles.loading}>Loading...</p>
      </div>
    );
  }

  if (!giftee) {
    return (
      <div className={styles.container}>
        <GifteeFlowHeader title={headerTitle} />
        <p className={styles.notFound}>Giftee not found.</p>
        <button
          type="button"
          className={styles.backLink}
          onClick={() => openCreateGifteeWizard(navigate, "/home")}
        >
          Create a giftee
        </button>
      </div>
    );
  }

  const occasion =
    (reminderTitle && reminderTitle.trim()) || "Gift";

  const interests = giftee ? parseInterestsFromGiftee(giftee) : new Set();
  const selectedPresetInterests = orderedSelectedPresetInterests(interests);
  const profileInterestChips = [...selectedPresetInterests, ...customInterests];
  const canRemoveAnyInterest = profileInterestChips.length > 1;

  const rot = rotationFromDrag(drag.x);
  const frontTransform = `translate(calc(-50% + ${drag.x}px), calc(-50% + ${drag.y}px)) rotate(${rot}deg)`;

  const showLike = drag.x > 36 && !exiting;
  const showPass = drag.x < -36 && !exiting;
  const next = deck[1];
  const top = deck[0];
  const showSwipeFeedback = Boolean(
    top && (showLike || showPass || exiting),
  );

  return (
    <div className={styles.container}>
      <GifteeFlowHeader title={headerTitle} />

      <div className={!top ? styles.screenWithCompletion : styles.screen}>
        {!top ? (
          <div className={styles.completionRoot}>
            <h2 className={styles.occasionTitle}>{occasion}</h2>
            <div className={styles.completionColumn}>
              <div className={styles.completionIntro}>
                <img
                  src="/images/GiftGaff_Logo.svg"
                  alt="Gift Gaff"
                  className={styles.completionLogo}
                />
                <p className={styles.completionLead}>
                  Help us improve recommendations.
                </p>
              </div>

              <section className={styles.completionSection}>
                <h2 className={profileStyles.sectionLabel}>Interests</h2>
                <div className={profileStyles.pillWrap}>
                  {profileInterestChips.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`${profileStyles.pill} ${profileStyles.pillSelected}`}
                      disabled={!canRemoveAnyInterest}
                      title={
                        canRemoveAnyInterest
                          ? "Remove interest"
                          : "At least one interest is required"
                      }
                      onClick={() => removeProfileInterest(item)}
                    >
                      {item}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={profileStyles.pill}
                    onClick={openInterestsMore}
                  >
                    <i className="fa-solid fa-plus" aria-hidden />
                    More
                  </button>
                  <span className={profileStyles.pill}>
                    <i className="fa-solid fa-plus" aria-hidden />
                    Custom
                  </span>
                </div>
              </section>

              <div className={styles.completionField}>
                <h2 className={profileStyles.sectionLabel}>Things to avoid</h2>
                <textarea
                  className={styles.completionTextarea}
                  value={thingsToAvoidDraft}
                  onChange={(e) => setThingsToAvoidDraft(e.target.value)}
                  placeholder="Type here"
                  rows={4}
                  aria-label="Things to avoid"
                />
              </div>

              <div className={styles.completionField}>
                <h2 className={profileStyles.sectionLabel}>Quick prompts</h2>
                <textarea
                  className={styles.completionTextarea}
                  value={quickPromptsDraft}
                  onChange={(e) => setQuickPromptsDraft(e.target.value)}
                  placeholder="Type here"
                  rows={4}
                  aria-label="Quick prompts"
                />
              </div>

              <button
                type="button"
                className={styles.completionSave}
                onClick={onRecoFeedbackSave}
                disabled={recoFeedbackSaveBusy}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className={styles.occasionTitle}>{occasion}</h2>
            <div className={styles.stackWrap}>
            {showSwipeFeedback && (
              <div className={styles.swipeFeedbackLayer} aria-hidden>
                <div
                  className={`${styles.overlay} ${showLike || exiting === "right" ? styles.overlayVisible : ""}`}
                >
                  <i className="fa-solid fa-thumbs-up" />
                </div>
                <div
                  className={`${styles.overlay} ${showPass || exiting === "left" ? styles.overlayVisible : ""}`}
                >
                  <i className="fa-solid fa-thumbs-down" />
                </div>
              </div>
            )}
            <div className={styles.stackInner}>
              <div className={styles.backPlate} aria-hidden />
              {next && (
                <GifteeRecoSwipeCard
                  key={`back-${next.id}`}
                  rec={next}
                  isDraggable={false}
                  className={`${styles.card} ${styles.cardBack}`}
                />
              )}
              <GifteeRecoSwipeCard
                key={`front-${top.id}`}
                rec={top}
                isDraggable
                className={`${styles.card} ${styles.cardFront} ${isDragging && !exiting ? styles.dragging : ""} ${exiting ? styles.exiting : ""} ${playFrontUnblur ? styles.cardFrontUnblur : ""}`}
                dragStyle={{ transform: frontTransform }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onTransitionEnd={onFrontTransitionEnd}
                onAnimationEnd={(e) => {
                  if (e.target !== e.currentTarget) return;
                  setPlayFrontUnblur(false);
                }}
              />
              {top && isLearnOpen && (
                <div className={styles.learnOverlay} role="dialog" aria-modal="true">
                  <div className={styles.learnPanel}>
                    <p className={styles.learnTitle}>Gift Recommendations</p>
                    <button
                      type="button"
                      className={styles.learnCloseBtn}
                      aria-label="Close swipe tutorial"
                      onClick={() => setIsLearnOpen(false)}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden />
                    </button>

                    <div className={styles.learnRows}>
                      <div className={styles.learnRow}>
                        <div className={styles.learnCardArt} aria-hidden>
                          <div className={`${styles.learnCard} ${styles.learnCardBack}`} />
                          <div className={`${styles.learnCard} ${styles.learnCardFront} ${styles.learnCardTiltRight}`}>
                            <div className={styles.learnTapDot}>
                              <i className="fa-solid fa-hand-pointer" />
                            </div>
                            <i className={`fa-solid fa-arrow-right ${styles.learnArrowRight}`} />
                          </div>
                        </div>
                        <p className={styles.learnCaption}>Swipe right to add to list.</p>
                      </div>

                      <div className={styles.learnRow}>
                        <div className={styles.learnCardArt} aria-hidden>
                          <div className={`${styles.learnCard} ${styles.learnCardBack}`} />
                          <div className={`${styles.learnCard} ${styles.learnCardFront}`}>
                            <div className={styles.learnTapDot}>
                              <i className="fa-solid fa-hand-pointer" />
                            </div>
                            <i className={`fa-solid fa-arrow-left ${styles.learnArrowLeft}`} />
                          </div>
                        </div>
                        <p className={styles.learnCaption}>Swipe left to dismiss.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
        )}
      </div>

      <GifteeInterestsMoreSheet
        open={interestsMoreOpen}
        interestsDraft={interestsDraft}
        onToggleChip={toggleDraftInterestChip}
        onClose={() => setInterestsMoreOpen(false)}
        onSave={saveInterestsMore}
      />
    </div>
  );
}
